import {
  ChangeEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from '@headlessui/react'

import useRequest from '@hooks/request'
import { useDebouncedCallback } from '@hooks/debounced-callback'
import { ComboInputProps, InputOption } from 'types/ui'
import { StatusSymbol } from './status-symbol'

const comboInputDisplayValue = (value: InputOption | null | undefined) =>
  value?.name ?? ''

export function ComboInput<T extends InputOption>({
  name,
  value,
  onChange,
  onSearch,
  options,
  disabled,
  placeholder,
  success,
  error,
  warning,
  className,
  type,
}: ComboInputProps<T>) {
  const comboboxInput = useRef<HTMLInputElement | null>(null)
  const comboboxButton = useRef<HTMLButtonElement | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [displayedOptions, setDisplayedOptions] = useState(options ?? [])

  const handleInputChange = useDebouncedCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value),
    [],
    250
  )

  const handleValueChange = useCallback<ComboInputProps<T>['onChange']>(
    async (val) => {
      await onChange?.(val)
    },
    [onChange]
  )

  /**
   * TODO: Test this functionality when hitting a real api
   */
  const [search, searching] = useRequest(
    async (query: string) => {
      if (onSearch !== undefined) {
        const requestResults = await onSearch(query)
        setDisplayedOptions(requestResults)
      } else {
        const filteredResults = options.reduce((results, result) => {
          const regex = new RegExp(`^(${query})`, 'gi')
          const matches = regex.test(result.name)
          if (matches) results.push(result)
          return results
        }, [] as T[])
        setDisplayedOptions(filteredResults)
      }
    },
    [onSearch, options]
  )

  useEffect(() => {
    if (!searchQuery || searchQuery === value?.name) {
      setDisplayedOptions(options ?? [])
    } else {
      search(searchQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  useEffect(() => {
    if (options) {
      setDisplayedOptions(options)
    }
  }, [options])

  const comboboxClassname = useComboboxClassname({
    className,
    success,
    error,
    warning,
  })

  return (
    <Combobox
      name={name}
      value={value}
      onChange={handleValueChange}
      disabled={disabled}
      multiple={false}
    >
      {({ open }) => (
        <div className="relative w-full">
          <div className="relative">
            <ComboboxInput
              type={type}
              placeholder={placeholder}
              displayValue={comboInputDisplayValue}
              disabled={disabled}
              onChange={handleInputChange}
              onFocus={() => {
                if (disabled) return
                comboboxButton.current?.click()
              }}
              ref={comboboxInput}
              className={comboboxClassname}
            />
            <ComboboxButton className="hidden" ref={comboboxButton} />
            <div className="absolute flex items-center justify-end space-x-2 -translate-y-1/2 right-3 top-1/2">
              {!disabled && (
                <StatusSymbol
                  error={error}
                  warning={warning}
                  success={success}
                />
              )}
            </div>
          </div>
          <Transition
            show={open}
            as={Fragment}
            enter="transition duration-150 ease-out"
            enterFrom="transform -translate-y-2 opacity-0"
            enterTo="transform translate-y-0 opacity-100"
            leave="transition duration-150 ease-out"
            leaveFrom="transform translate-y-0 opacity-100"
            leaveTo="transform -translate-y-2 opacity-0"
          >
            <ComboboxOptions
              modal={false}
              className={classNames(
                'left-0 right-0 mt-1 mx-0 !p-0 border input-menu-border rounded popover-shadow cursor-auto h-fit max-h-60 overflow-y-auto',
                'z-[1] absolute overflow-clip border-fg border-opacity-20 dark:border-opacity-20 bg-stone-100 dark:bg-stone-800'
              )}
            >
              {displayedOptions.map((option) => (
                <ComboboxOption
                  key={option.id}
                  value={option}
                  disabled={option.unavailable}
                  className="input-menu-item"
                >
                  <span>{option.name}</span>
                  <CheckCircleIcon className="input-menu-item-icon" />
                </ComboboxOption>
              ))}
              {searching && (
                <span className="p-4 text-base text-thin">Searching...</span>
              )}
              {!searching && !displayedOptions.length && (
                <li className="w-full p-4 text-base italic text-center">
                  No results
                </li>
              )}
            </ComboboxOptions>
          </Transition>
        </div>
      )}
    </Combobox>
  )
}

function useComboboxClassname({
  className,
  success,
  warning,
  error,
}: {
  className?: string
  success?: boolean
  warning?: boolean
  error?: boolean
}) {
  return useMemo(
    () =>
      classNames(className, 'input-styles !px-3 py-[5px]', {
        'state-primary': !(success || warning || error),
        'state-error': error,
        'state-warning': warning && !error,
        'state-success': success && !error && !warning,
      }),
    [className, error, success, warning]
  )
}
